import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExternalLink,
  MessageCircle,
  Heart,
  Share2,
  Eye,
  Check,
  Clock,
  User,
  BadgeCheck
} from 'lucide-react';
import { Mention } from '../../services/listeningService';
import SentimentBadge from './SentimentBadge';
import Button from '../ui/Button';

interface MentionCardProps {
  mention: Mention;
  onMarkResponded?: (mentionId: string) => void;
  onViewOriginal?: (url: string) => void;
}

const platformIcons: Record<string, string> = {
  twitter: 'X',
  instagram: 'IG',
  facebook: 'FB',
  linkedin: 'in',
  youtube: 'YT',
  reddit: 'R',
  news: 'N',
  blog: 'B'
};

const platformColors: Record<string, string> = {
  twitter: 'bg-black text-white',
  instagram: 'bg-gradient-to-br from-purple-500 to-pink-500 text-white',
  facebook: 'bg-blue-600 text-white',
  linkedin: 'bg-blue-700 text-white',
  youtube: 'bg-red-600 text-white',
  reddit: 'bg-orange-500 text-white',
  news: 'bg-gray-600 text-white',
  blog: 'bg-green-600 text-white'
};

const MentionCard: React.FC<MentionCardProps> = ({
  mention,
  onMarkResponded,
  onViewOriginal
}) => {
  const { t } = useTranslation();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      return t('common.justNow', 'Just now');
    } else if (diffHours < 24) {
      return t('common.hoursAgo', '{{count}}h ago').replace('{{count}}', String(diffHours));
    } else if (diffDays < 7) {
      return t('common.daysAgo', '{{count}}d ago').replace('{{count}}', String(diffDays));
    } else {
      return date.toLocaleDateString(undefined, {
        day: '2-digit',
        month: 'short'
      });
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3">
          {/* Author Avatar */}
          <div className="relative">
            {mention.author_avatar ? (
              <img
                src={mention.author_avatar}
                alt={mention.author_name || ''}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <User className="w-5 h-5 text-gray-400" />
              </div>
            )}
            {/* Platform Badge */}
            <div
              className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                platformColors[mention.platform] || 'bg-gray-500 text-white'
              }`}
            >
              {platformIcons[mention.platform] || 'P'}
            </div>
          </div>

          {/* Author Info */}
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-gray-900 dark:text-white">
                {mention.author_name || t('listening.user', 'User')}
              </span>
              {mention.is_influencer && (
                <span title={t('listening.influencer', 'Influencer')}>
                  <BadgeCheck className="w-4 h-4 text-blue-500" />
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span>{mention.author_handle}</span>
              {mention.author_followers > 0 && (
                <>
                  <span>·</span>
                  <span>{formatNumber(mention.author_followers)} {t('listening.followersCount', 'followers')}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Timestamp & Sentiment */}
        <div className="flex flex-col items-end gap-1">
          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDate(mention.mentioned_at)}
          </span>
          <SentimentBadge sentiment={mention.sentiment} size="sm" />
        </div>
      </div>

      {/* Content */}
      <p className="text-gray-900 dark:text-white mb-3 whitespace-pre-wrap">
        {mention.content}
      </p>

      {/* Media */}
      {mention.media_urls && mention.media_urls.length > 0 && (
        <div className="mb-3 rounded-lg overflow-hidden">
          <img
            src={mention.media_urls[0]}
            alt=""
            className="w-full h-48 object-cover"
          />
        </div>
      )}

      {/* Brand Tag */}
      {mention.brand && (
        <div className="mb-3">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              mention.brand.type === 'own'
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
            }`}
          >
            {mention.brand.name}
          </span>
        </div>
      )}

      {/* Engagement Stats */}
      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
        <span className="flex items-center gap-1">
          <Heart className="w-4 h-4" />
          {formatNumber(mention.engagement?.likes || 0)}
        </span>
        <span className="flex items-center gap-1">
          <MessageCircle className="w-4 h-4" />
          {formatNumber(mention.engagement?.comments || 0)}
        </span>
        <span className="flex items-center gap-1">
          <Share2 className="w-4 h-4" />
          {formatNumber(mention.engagement?.shares || 0)}
        </span>
        <span className="flex items-center gap-1">
          <Eye className="w-4 h-4" />
          {formatNumber(mention.reach_estimate || 0)}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          {mention.requires_response && !mention.responded && (
            <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
              {t('listening.requiresResponse', 'Requires response')}
            </span>
          )}
          {mention.responded && (
            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 flex items-center gap-1">
              <Check className="w-3 h-3" />
              {t('listening.responded', 'Responded')}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!mention.responded && onMarkResponded && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onMarkResponded(mention.id)}
              className="text-xs"
            >
              <Check className="w-3 h-3 mr-1" />
              {t('listening.markResponded', 'Mark responded')}
            </Button>
          )}
          {mention.url && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(mention.url, '_blank')}
              className="text-xs"
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              {t('listening.viewOriginal', 'View original')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MentionCard;
